import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  ChevronRight,
  Mail,
  MessageCircle,
  Phone,
  Send,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import assetUrl from '../assetUrl';
import './ContactPage.css';

const BANNER_IMAGE = '/images/home-zigzag-1.jpg';
const WHATSAPP_URL = 'https://wa.me/21671234567';

export default function ContactPage() {
  const { t, isRTL } = useLanguage();
  const c = t.contact;
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError(t.auth?.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setError('');
    setSent(true);
  };

  const resetForm = () => {
    setForm({ name: '', email: '', phone: '', message: '' });
    setError('');
    setSent(false);
  };

  return (
    <div className={`contact-page${isRTL ? ' rtl' : ''}`}>
      <header className="contact-banner">
        <div
          className="contact-banner-bg"
          style={{ backgroundImage: `url(${assetUrl(BANNER_IMAGE)})` }}
          aria-hidden="true"
        />
        <div className="contact-banner-veil" aria-hidden="true" />

        <div className="container contact-banner-inner">
          <span className="contact-banner-badge">{c.badge}</span>
          <h1>{c.title}</h1>
          <p>{c.subtitle}</p>
          <nav className="contact-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">{c.breadcrumbHome}</Link>
            <ChevronRight size={12} aria-hidden="true" />
            <span>{c.title}</span>
          </nav>
        </div>
      </header>

      <section className="contact-body">
        <div className="container contact-layout">
          <aside className="contact-info">
            <h2 className="contact-info-title">{c.infoTitle}</h2>

            <a className="contact-card" href="tel:+21671234567">
              <span className="contact-card-icon">
                <Phone size={18} />
              </span>
              <span className="contact-card-text">
                <small>{c.phoneLabel}</small>
                <strong>+216 71 234 567</strong>
                <span>+216 98 765 432</span>
              </span>
            </a>

            <a className="contact-card" href="mailto:contact@tarjemlek.tn">
              <span className="contact-card-icon">
                <Mail size={18} />
              </span>
              <span className="contact-card-text">
                <small>{c.emailLabel}</small>
                <strong>contact@tarjemlek.tn</strong>
                <span>info@tarjemlek.tn</span>
              </span>
            </a>

            <a
              className="contact-whatsapp"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
            >
              <MessageCircle size={18} />
              {c.whatsapp}
            </a>
          </aside>

          <div className="contact-form-card">
            {sent ? (
              <div className="contact-success">
                <span className="contact-success-icon">
                  <CheckCircle size={38} />
                </span>
                <p>{c.success}</p>
                <button type="button" className="btn btn-outline" onClick={resetForm}>
                  {c.successAgain}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <header className="contact-form-head">
                  <h2>{c.formTitle}</h2>
                  <p>{c.formHint}</p>
                </header>

                <div className="contact-form-grid">
                  <label className="contact-field">
                    <span>{c.name}</span>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </label>
                  <label className="contact-field">
                    <span>{c.email}</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </label>
                  <label className="contact-field contact-field-full">
                    <span>
                      {c.phone} <small>({c.optional})</small>
                    </span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </label>
                  <label className="contact-field contact-field-full contact-field-message">
                    <span>{c.message}</span>
                    <textarea
                      rows={7}
                      placeholder={c.messagePlaceholder}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                    />
                  </label>
                </div>

                {error && <p className="auth-error" role="alert">{error}</p>}
                <button type="submit" className="btn btn-primary contact-submit">
                  <Send size={17} />
                  {c.send}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
